/* eslint-disable jsx-a11y/label-has-associated-control */
import type { ChangeEvent, DetailedHTMLProps, FocusEvent, InputHTMLAttributes } from 'react';
import React, { useState } from 'react';
import type { _Omit, Merge, RequireKeys } from '@noths/polaris-dev-ts-recipes';

import * as styles from './styles';
import type { InputFeedback } from 'client-ribbons-design-system/components/elements/Feedback/Feedback';
import { TextField } from 'client-ribbons-design-system/components/elements/TextField/TextField';

type TextAreaChangeEvent = ChangeEvent<HTMLTextAreaElement>;
type AugmentedChangeNativeEvent = TextAreaChangeEvent['nativeEvent'] & {
  /**
   * Property type doesn't exist in current version of `@types/react`.
   *
   * For description, see: https://developer.mozilla.org/en-US/docs/Web/API/InputEvent/inputType
   */
  inputType: string;
};
type AugmentedTextAreaChangeEvent = TextAreaChangeEvent & {
  nativeEvent: AugmentedChangeNativeEvent;
};

interface OwnProps {
  description?: string;
  error?: string | null;
  feedback: InputFeedback;
  onChange: (e: AugmentedTextAreaChangeEvent) => void;
  value: string;
}

// Omit "maxLength" & require keys: "id" | "name" | "onChange"
type TextAreaProps = RequireKeys<
  _Omit<
    DetailedHTMLProps<InputHTMLAttributes<HTMLTextAreaElement>, HTMLTextAreaElement>,
    'maxLength'
  >,
  'id' | 'name' | 'onChange'
>;

export type EntryFieldProps = Merge<TextAreaProps, OwnProps>;

export const EntryField = ({
  description,
  error,
  feedback,
  id,
  name,
  onBlur,
  onChange,
  required,
  value = '',
  ...props
}: EntryFieldProps) => {
  const [textareaHasKeyboardFocus, setTextareaHasKeyboardFocus] = useState(false);

  const isValid = !textareaHasKeyboardFocus && value && !error;

  const handleTextAreaBlur = (event: FocusEvent<HTMLTextAreaElement>) => {
    setTextareaHasKeyboardFocus(false);
    onBlur?.(event);
  };

  return (
    <div
      css={styles.entryFieldContainer}
      data-has-error={!!error}
      data-has-focus={textareaHasKeyboardFocus}
      data-is-valid={isValid}
    >
      {required && (
        <p
          aria-hidden="true"
          css={styles.requiredLabel}
          data-has-error={!!error}
          data-is-valid={isValid}
        >
          Required
        </p>
      )}

      <TextField
        description={description}
        error={error}
        feedback={feedback}
        id={id}
        label={name}
        name={name}
        onBlur={handleTextAreaBlur}
        onChange={onChange}
        onFocus={() => setTextareaHasKeyboardFocus(true)}
        required={required}
        value={value}
        {...props}
      />
    </div>
  );
};
